import { useState, useCallback, MutableRefObject } from 'react';
import { EditSuggestion, ChatMessage } from '../services/chatService';
import { versionService } from '../services/elaborationService';
import { roadmapService } from '../services/roadmapService';
import { checklistService } from '../services/checklistService';
import { documentChecklistService } from '../services/documentChecklistService';
import { RoadmapContextItem } from '../services/chatContextService';

interface UseChatInlineEditOptions {
  proposalId?: number | null;
  conceptMapRef: MutableRefObject<Record<string, unknown>>;
  checklistItemsRef: MutableRefObject<Record<string, unknown>[]>;
  docChecklistItemsRef: MutableRefObject<Record<string, unknown>[]>;
  roadmapItemsRef: MutableRefObject<RoadmapContextItem[]>;
  addMessage: (msg: ChatMessage) => void;
  onContextInvalidated?: () => void;
}

export function useChatInlineEdit({
  proposalId,
  conceptMapRef,
  checklistItemsRef,
  docChecklistItemsRef,
  roadmapItemsRef,
  addMessage,
  onContextInvalidated,
}: UseChatInlineEditOptions) {
  const [pendingEdit, setPendingEdit] = useState<EditSuggestion | null>(null);
  const [applyingEdit, setApplyingEdit] = useState(false);

  /** Retorna o conteúdo atual de uma seção para mostrar o diff. */
  const getCurrentContent = useCallback((suggestion: EditSuggestion): string | null => {
    if (suggestion.edit_type === 'elaboration_content' && suggestion.roadmap_id) {
      const item = roadmapItemsRef.current.find(r => r.id === suggestion.roadmap_id);
      return item?.content || null;
    }
    if (suggestion.edit_type === 'roadmap_instruction' && suggestion.roadmap_id) {
      const item = roadmapItemsRef.current.find(r => r.id === suggestion.roadmap_id);
      return item?.instruction || null;
    }
    if (suggestion.edit_type === 'concept_map_field' && suggestion.field) {
      const val = conceptMapRef.current[suggestion.field];
      return val ? String(val) : null;
    }
    return null;
  }, [roadmapItemsRef, conceptMapRef]);

  const apply = useCallback(async () => {
    if (!pendingEdit || applyingEdit) return;
    const s = pendingEdit;
    setPendingEdit(null);
    setApplyingEdit(true);

    try {
      if (s.edit_type === 'elaboration_content' && s.roadmap_id) {
        window.dispatchEvent(new CustomEvent('chatia:applying-edit', { detail: { roadmapId: s.roadmap_id, topic: s.topic } }));
        // O histórico de versões não substitui o conteúdo corrente do roteiro. Salve
        // os dois registros para que uma nova montagem de contexto leia esta versão,
        // inclusive no próximo pedido da mesma conversa.
        await versionService.saveManual(s.roadmap_id, s.proposed_content);
        await roadmapService.update(s.roadmap_id, { content: s.proposed_content });
        // Atualiza ref local
        const idx = roadmapItemsRef.current.findIndex(r => r.id === s.roadmap_id);
        if (idx !== -1) roadmapItemsRef.current[idx].content = s.proposed_content;
        window.dispatchEvent(new CustomEvent('chatia:edit-applied', { detail: { roadmapId: s.roadmap_id, topic: s.topic, newContent: s.proposed_content } }));
        addMessage({ role: 'assistant', content: `✅ Conteúdo da seção **${s.topic}** aplicado na aba Elaboração.` });

      } else if (s.edit_type === 'roadmap_instruction' && s.roadmap_id) {
        window.dispatchEvent(new CustomEvent('chatia:applying-roadmap-instruction', { detail: { roadmapId: s.roadmap_id, topic: s.topic } }));
        await roadmapService.update(s.roadmap_id, { instruction: s.proposed_content });
        const idx = roadmapItemsRef.current.findIndex(r => r.id === s.roadmap_id);
        if (idx !== -1) roadmapItemsRef.current[idx].instruction = s.proposed_content;
        window.dispatchEvent(new CustomEvent('chatia:roadmap-instruction-applied', { detail: { roadmapId: s.roadmap_id, topic: s.topic, newInstruction: s.proposed_content } }));
        addMessage({ role: 'assistant', content: `✅ Instrução da seção **${s.topic}** atualizada no Roteiro.` });

      } else if (s.edit_type === 'concept_map_field' && s.field) {
        conceptMapRef.current[s.field] = s.proposed_content;
        window.dispatchEvent(new CustomEvent('chatia:applying-concept-field', { detail: { field: s.field, fieldLabel: s.field_label, value: s.proposed_content } }));
        addMessage({ role: 'assistant', content: `✅ Campo **${s.field_label || s.field}** das Orientações atualizado.` });

      } else if (s.edit_type === 'checklist_item_add' && proposalId) {
        const type = s.checklist_type || 'enquadramento';
        let newItem: any;
        if (type === 'documental') {
          newItem = await documentChecklistService.create(proposalId, s.proposed_content, 9999, s.group || null);
          docChecklistItemsRef.current = [...docChecklistItemsRef.current, { id: newItem.id, text: newItem.text, item_group: newItem.item_group ?? null, evaluation_level: null }];
        } else {
          newItem = await checklistService.create(proposalId, s.proposed_content, 9999, s.group || null);
          checklistItemsRef.current = [...checklistItemsRef.current, { id: newItem.id, text: newItem.text, item_group: newItem.item_group ?? null, evaluation_level: null }];
        }
        window.dispatchEvent(new CustomEvent('chatia:checklist-item-added', { detail: { checklist_type: type, newItem } }));
        addMessage({ role: 'assistant', content: `✅ Critério adicionado ao checklist de ${type === 'documental' ? 'documental' : 'enquadramento'}.` });

      } else if (s.edit_type === 'checklist_evaluation' && s.item_id && s.evaluation_level) {
        const type = s.checklist_type || 'enquadramento';
        const evalLevel = s.evaluation_level as any;
        if (type === 'documental') {
          await documentChecklistService.update(s.item_id, { evaluation_level: evalLevel });
        } else {
          await checklistService.update(s.item_id, { evaluation_level: evalLevel });
        }
        window.dispatchEvent(new CustomEvent('chatia:checklist-evaluation-updated', { detail: { checklist_type: type, itemId: s.item_id, evaluation_level: evalLevel } }));
        addMessage({ role: 'assistant', content: `✅ Avaliação de "${s.item_text || 'item'}" definida como **${evalLevel}**.` });
      }

      onContextInvalidated?.();
    } catch {
      addMessage({ role: 'assistant', content: `⚠️ Não foi possível aplicar a alteração. Tente novamente.` });
      if (s.roadmap_id) {
        window.dispatchEvent(new CustomEvent('chatia:edit-failed', { detail: { roadmapId: s.roadmap_id } }));
      }
    } finally {
      setApplyingEdit(false);
    }
  }, [pendingEdit, applyingEdit, proposalId, conceptMapRef, checklistItemsRef, docChecklistItemsRef, roadmapItemsRef, addMessage, onContextInvalidated]);

  const reject = useCallback(() => {
    if (!pendingEdit) return;
    const ref = pendingEdit.topic || pendingEdit.field_label || pendingEdit.field || pendingEdit.item_text || 'item';
    setPendingEdit(null);
    addMessage({ role: 'assistant', content: `Entendido. A sugestão para **${ref}** foi descartada. Posso ajudar com outra coisa?` });
  }, [pendingEdit, addMessage]);

  return { pendingEdit, setPendingEdit, applyingEdit, apply, reject, getCurrentContent };
}
